import type { BridgeContext } from '../types';
import type { TBridgeMethod, TBridgeMiddleware } from '../methods/types';

/**
 * The subset of BridgeContext fields that an embedded macro iframe is allowed
 * to override. These are identity/routing fields only — services, bridgeConfig,
 * and bridgeControls always remain from the host page's BridgeContext.
 */
export type EmbeddedMacroContext = Required<
	Pick<
		BridgeContext,
		| 'contextIds'
		| 'extension'
		| 'extensionDetails'
		| 'extensionPayload'
		| 'page'
		| 'resolverContext'
		| 'viewContext'
	>
>;

type EmbeddedMacroPayload = {
	embeddedMacroContext: EmbeddedMacroContext;
	payload: unknown;
};

function isEmbeddedMacroPayload(value: unknown): value is EmbeddedMacroPayload {
	return typeof value === 'object' && value !== null && 'embeddedMacroContext' in value;
}

export const withEmbeddedMacroContext: TBridgeMiddleware =
	<Payload, Result>(
		method: TBridgeMethod<BridgeContext, Payload, Result>,
		_methodName: string,
	): TBridgeMethod<BridgeContext, Payload, Result> =>
	async (context, embeddedMacroPayload) => {
		if (!isEmbeddedMacroPayload(embeddedMacroPayload)) {
			return method(context, embeddedMacroPayload);
		}

		const {
			embeddedMacroContext: {
				contextIds,
				extension,
				extensionDetails,
				extensionPayload,
				page,
				resolverContext,
				viewContext,
			},
			payload,
		} = embeddedMacroPayload;

		const mergedContext: BridgeContext = {
			...context,
			contextIds,
			extension,
			extensionDetails,
			extensionPayload,
			page,
			resolverContext,
			viewContext,
		};

		// `payload` is `unknown` from the embedded macro payload — cast to Payload since this middleware
		// intentionally unwraps it and forwards the inner args as-is to the method.
		return method(mergedContext, payload as Payload);
	};
